"use client";

import type React from "react";
import { useState, useRef, useCallback } from "react";
import {
  Box,
  Heading,
  Text,
  VStack,
  HStack,
  Button,
  IconButton,
  Badge,
  Divider,
  useToast,
} from "@chakra-ui/react";
import { DeleteIcon } from "@chakra-ui/icons";
import { FileText, Link, FileUp } from "lucide-react";
import {
  knowledgeBaseService,
  type KnowledgeBase,
  type KnowledgeBaseText,
} from "../../services/knowledgeBaseService.ts";
import { useAppTheme } from "../../ThemeContext.tsx";
import { AddTextModal } from "./AddTextModal.tsx";
import { AddWebPagesModal } from "./AddWebPagesModal.tsx";
import useInterval from "../../hooks/useInterval.ts";

interface KnowledgeBaseDetailsProps {
  knowledgeBase: KnowledgeBase;
  onRefresh: (updatedKnowledgeBase?: KnowledgeBase) => void;
  isPolling: boolean;
  setIsPolling: React.Dispatch<React.SetStateAction<boolean>>;
}

export const KnowledgeBaseDetails: React.FC<KnowledgeBaseDetailsProps> = ({
  knowledgeBase,
  onRefresh,
  isPolling,
  setIsPolling,
}) => {
  const MAX_FILES = 25;
  const MAX_FILE_SIZE = 50 * 1024 * 1024;

  const [isLoading, setIsLoading] = useState(false);
  const [isAddingText, setIsAddingText] = useState(false);
  const [isAddingWebPages, setIsAddingWebPages] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const toast = useToast();
  const { resolvedTheme } = useAppTheme();
  const isDark = resolvedTheme === "dark";

  const handleAddText = async (text: KnowledgeBaseText) => {
    setIsLoading(true);
    try {
      await knowledgeBaseService.addSources(
        knowledgeBase.knowledge_base_id,
        [text],
        undefined,
        undefined
      );
      toast({
        title: "Source added successfully",
        status: "success",
        duration: 3000,
      });
      const updatedKnowledgeBase = await knowledgeBaseService.get(
        knowledgeBase.knowledge_base_id
      );
      onRefresh(updatedKnowledgeBase);
      setIsPolling(true);
    } catch (error: any) {
      toast({
        title: "Error adding text",
        description:
          error.response?.data?.message || "An unexpected error occurred",
        status: "error",
        duration: 5000,
      });
    } finally {
      setIsLoading(false);
      setIsAddingText(false);
    }
  };

  const handleAddWebPages = async (urls: string[]) => {
    setIsLoading(true);
    try {
      if (urls.length === 0) {
        throw new Error("No URLs provided");
      }
      await knowledgeBaseService.addSources(
        knowledgeBase.knowledge_base_id,
        undefined,
        undefined,
        urls
      );

      toast({
        title: "Source added successfully",
        status: "success",
        duration: 3000,
      });
      const updatedKnowledgeBase = await knowledgeBaseService.get(
        knowledgeBase.knowledge_base_id
      );
      onRefresh(updatedKnowledgeBase);
      setIsPolling(true);
    } catch (error: any) {
      console.error("Error adding web pages:", error);
      toast({
        title: "Error adding web pages",
        description:
          error.response?.data?.message || "An unexpected error occurred",
        status: "error",
        duration: 5000,
      });
    } finally {
      setIsLoading(false);
      setIsAddingWebPages(false);
    }
  };

  const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    if (!e.target.files || e.target.files.length === 0) return;
    if (e.target.files.length > MAX_FILES) {
      alert(`Please select at most ${MAX_FILES} files.`);
      if (fileInputRef.current) fileInputRef.current.value = "";
      return;
    }
    for (const file of e.target.files) {
      if (file.size > MAX_FILE_SIZE) {
        alert(`File "${file.name}" exceeds 50MB limit.`);
        if (fileInputRef.current) fileInputRef.current.value = "";
        return;
      }
    }
    setIsLoading(true);
    try {
      const files = Array.from(e.target.files);

      await knowledgeBaseService.addSources(
        knowledgeBase.knowledge_base_id,
        undefined,
        files,
        undefined
      );
      toast({
        title: "Source added successfully",
        status: "success",
        duration: 3000,
      });
      const updatedKnowledgeBase = await knowledgeBaseService.get(
        knowledgeBase.knowledge_base_id
      );
      onRefresh(updatedKnowledgeBase);
      setIsPolling(true);
    } catch (error: any) {
      console.error("Error adding web pages:", error);
      toast({
        title: "Error adding files",
        description:
          error.response?.data?.message || "An unexpected error occurred",
        status: "error",
        duration: 5000,
      });
    } finally {
      setIsLoading(false);
      if (fileInputRef.current) fileInputRef.current.value = "";
    }
  };
  const handleDeleteSource = async (sourceId: string) => {
    setIsLoading(true);
    try {
      const updatedKnowledgeBase = await knowledgeBaseService.removeSource(
        knowledgeBase.knowledge_base_id,
        sourceId
      );
      toast({
        title: "Source removed successfully",
        status: "success",
        duration: 3000,
      });
      onRefresh(updatedKnowledgeBase);
    } catch (error: any) {
      toast({
        title: "Error removing source",
        description:
          error.response?.data?.message || "An unexpected error occurred",
        status: "error",
        duration: 5000,
      });
    } finally {
      setIsLoading(false);
    }
  };

  const getStatusBadge = () => {
    switch (knowledgeBase.status) {
      case "in_progress":
        return (
          <Badge
            colorScheme="yellow"
            backgroundColor={isDark ? "yellow.400" : "yellow.30"}
            color={isDark ? "white" : "yellow.400"}
          >
            Uploading
          </Badge>
        );
      case "complete":
        return (
          <Badge
            colorScheme="green"
            backgroundColor={isDark ? "green.400" : "green.30"}
            color={isDark ? "white" : "green.400"}
          >
            Ready to use
          </Badge>
        );
      case "error":
        return (
          <Badge
            colorScheme="red"
            backgroundColor={isDark ? "red.400" : "red.30"}
            color={isDark ? "white" : "red.400"}
          >
            Error
          </Badge>
        );
      case "refreshing_in_progress":
        return (
          <Badge
            colorScheme="blue"
            backgroundColor={isDark ? "blue.500" : "blue.100"}
            color={isDark ? "white" : "blue.700"}
          >
            Refreshing
          </Badge>
        );
      default:
        return (
          <Badge colorScheme="gray">{knowledgeBase.status}</Badge>
        );
    }
  };

  const getSourceIcon = (type: string) => {
    switch (type) {
      case "text":
        return <FileText size={16} />;
      case "document":
        return <FileUp size={16} />;
      case "url":
        return <Link size={16} />;
      default:
        return null;
    }
  };

  const pollKnowledgeBase = useCallback(async () => {
    try {
      const updatedKnowledgeBase = await knowledgeBaseService.get(
        knowledgeBase.knowledge_base_id
      );
      onRefresh(updatedKnowledgeBase);

      // Only stop polling if the status is 'complete' or 'error'
      if (
        updatedKnowledgeBase.status === "complete" ||
        updatedKnowledgeBase.status === "error"
      ) {
        setIsPolling(false);
      }
    } catch (error) {
      console.error("Error polling knowledge base status:", error);
      setIsPolling(false);
    }
  }, [knowledgeBase.knowledge_base_id, onRefresh, setIsPolling]);

  useInterval(
    pollKnowledgeBase,
    isPolling ||
      knowledgeBase.status === "in_progress" ||
      knowledgeBase.status === "refreshing_in_progress"
      ? 5000
      : null
  );

  return (
    <Box p={4}>
      <VStack spacing={3} align="stretch">
        <HStack justify="space-between">
          <Heading size="md" color={isDark ? "white" : "gray.800"}>
            {knowledgeBase.knowledge_base_name}
          </Heading>
          {getStatusBadge()}
        </HStack>

        <HStack spacing={4}>
          <Button
            leftIcon={<Link size={16} />}
            size="sm"
            variant="outline"
            onClick={() => setIsAddingWebPages(true)}
            isLoading={isLoading}
            color={isDark ? "gray.300" : "gray.700"}
            borderColor={isDark ? "gray.600" : "gray.300"}
            _hover={{ bg: isDark ? "gray.700" : "gray.100" }}
          >
            Add Web Pages
          </Button>
          <Button
            leftIcon={<FileUp size={16} />}
            size="sm"
            variant="outline"
            onClick={() => fileInputRef.current?.click()}
            isLoading={isLoading}
            color={isDark ? "gray.300" : "gray.700"}
            borderColor={isDark ? "gray.600" : "gray.300"}
            _hover={{ bg: isDark ? "gray.700" : "gray.100" }}
          >
            Upload Files
          </Button>
          <Button
            leftIcon={<FileText size={16} />}
            size="sm"
            variant="outline"
            onClick={() => setIsAddingText(true)}
            isLoading={isLoading}
            color={isDark ? "gray.300" : "gray.700"}
            borderColor={isDark ? "gray.600" : "gray.300"}
            _hover={{ bg: isDark ? "gray.700" : "gray.100" }}
          >
            Add Text
          </Button>
          <input
            type="file"
            ref={fileInputRef}
            style={{ display: "none" }}
            onChange={handleFileChange}
            multiple
            accept=".pdf,.doc,.docx,.txt,.csv,.xls,.xlsx,.ppt,.pptx"
          />
        </HStack>

        <Divider borderColor={isDark ? "gray.700" : "gray.200"} />

        <VStack spacing={4} align="stretch">
          <Heading size="sm" color={isDark ? "white" : "gray.800"}>
            Sources
          </Heading>

          {(knowledgeBase.knowledge_base_sources?.length ?? 0) === 0 ? (
            <Text color={isDark ? "gray.400" : "gray.600"}>
              No sources added yet
            </Text>
          ) : (
            (knowledgeBase.knowledge_base_sources ?? []).map((source) => (
              <HStack
                key={source.source_id}
                p={3}
                bg={isDark ? "gray.700" : "gray.100"}
                borderRadius="md"
                justify="space-between"
              >
                <HStack>
                  {getSourceIcon(source.type)}
                  <VStack align="start" spacing={0}>
                    <Text
                      fontWeight="medium"
                      color={isDark ? "white" : "gray.800"}
                      fontSize="sm"
                    >
                      {source.type === "text"
                        ? source.title
                        : source.type === "document"
                        ? source.filename
                        : source.url}
                    </Text>
                    <Text
                      fontSize="xs"
                      color={isDark ? "gray.400" : "gray.600"}
                    >
                      {source.type.charAt(0).toUpperCase() +
                        source.type.slice(1)}
                    </Text>
                  </VStack>
                </HStack>
                <IconButton
                  aria-label="Delete source"
                  icon={<DeleteIcon />}
                  size="sm"
                  variant="ghost"
                  onClick={() => handleDeleteSource(source.source_id)}
                  isDisabled={isLoading}
                  color={isDark ? "red.300" : "red.500"}
                  _hover={{ bg: isDark ? "red.800" : "red.50" }}
                />
              </HStack>
            ))
          )}
        </VStack>
      </VStack>

      <AddTextModal
        isOpen={isAddingText}
        onClose={() => setIsAddingText(false)}
        onSave={handleAddText}
      />

      <AddWebPagesModal
        isOpen={isAddingWebPages}
        onClose={() => setIsAddingWebPages(false)}
        onSave={handleAddWebPages}
      />
    </Box>
  );
};
